import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Wand2, Download, Sparkles, Upload, Lock } from "lucide-react";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";
import UpgradeModal from "../components/UpgradeModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PostProduction() {
  const { recordingId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isPro } = useAuth();
  const [recording, setRecording] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [editInstruction, setEditInstruction] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [manualTranscript, setManualTranscript] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const videoRef = useRef(null);
  const [introClip, setIntroClip] = useState(null);
  const [outroClip, setOutroClip] = useState(null);
  const [backgroundMusic, setBackgroundMusic] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    loadRecording();
  }, [recordingId]);

  const loadRecording = async () => {
    try {
      // Fetch recording metadata from backend
      const recordingResponse = await axios.get(`${API}/recordings?user_id=${currentUser.uid}`);
      const recordings = recordingResponse.data;
      const currentRecording = recordings.find(r => r.id === recordingId);
      
      if (currentRecording) {
        setRecording(currentRecording);
        
        // Try to load video from sessionStorage
        const videoData = sessionStorage.getItem(`recording_${recordingId}`);
        if (videoData && videoRef.current) {
          videoRef.current.src = videoData;
          console.log('✅ Video loaded from storage');
        } else {
          console.warn('⚠️ No video data found in storage');
          toast.warning('Video not found. Please record again.');
        }
      } else {
        toast.error('Recording not found');
      }
      
      // Try to load transcript if it exists
      try {
        const transcriptResponse = await axios.get(`${API}/transcripts/${recordingId}?user_id=${currentUser.uid}`);
        setTranscript(transcriptResponse.data);
      } catch (error) {
        // Transcript doesn't exist yet
        console.log('No transcript found');
      }
    } catch (error) {
      console.error('Error loading recording:', error);
      toast.error('Failed to load recording');
    }
  };

  const createTranscript = async () => {
    if (!manualTranscript) {
      toast.error('Please enter a transcript');
      return;
    }

    try {
      const response = await axios.post(`${API}/transcripts`, {
        user_id: currentUser.uid,
        recording_id: recordingId,
        content: manualTranscript
      });
      
      setTranscript(response.data);
      setManualTranscript("");
      toast.success('Transcript created');
    } catch (error) {
      console.error('Error creating transcript:', error);
      toast.error('Failed to create transcript');
    }
  };

  const editTranscript = async () => {
    if (!isPro) {
      setShowUpgradeModal(true);
      return;
    }

    if (!editInstruction || !transcript) {
      toast.error('Please enter an edit instruction');
      return;
    }

    setIsEditing(true);
    try {
      const response = await axios.post(`${API}/transcripts/edit?user_id=${currentUser.uid}`, {
        transcript_id: transcript.id,
        instruction: editInstruction
      });
      
      setTranscript({
        ...transcript,
        edited_content: response.data.edited_content
      });
      
      setEditInstruction("");
      toast.success('Transcript edited successfully');
    } catch (error) {
      console.error('Error editing transcript:', error);
      toast.error('Failed to edit transcript');
    } finally {
      setIsEditing(false);
    }
  };

  const enhanceAudio = async () => {
    if (!isPro) {
      setShowUpgradeModal(true);
      return;
    }

    if (!videoRef.current || !videoRef.current.src) {
      toast.error('No video source available to enhance');
      return;
    }
    
    setIsEnhancing(true);
    
    try {
      // Get video blob from the demo video URL
      toast.info('Downloading video for processing...');
      const videoResponse = await fetch(videoRef.current.src);
      const videoBlob = await videoResponse.blob();
      
      // Create form data
      const formData = new FormData();
      formData.append('file', videoBlob, 'video.mp4');
      
      toast.info('Enhancing audio with AI... This may take a moment.');
      
      // Send to backend for processing
      const response = await axios.post(`${API}/process/enhance-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      // Download the enhanced file
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `enhanced_audio_${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('✅ Audio enhanced! Noise reduced, levels optimized, echo removed. File downloaded.');
    } catch (error) {
      console.error('Audio enhancement error:', error);
      toast.error('Audio enhancement failed. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const enhanceVideo = async () => {
    if (!isPro) {
      setShowUpgradeModal(true);
      return;
    }

    if (!videoRef.current || !videoRef.current.src) {
      toast.error('No video source available to enhance');
      return;
    }
    
    setIsEnhancing(true);
    
    try {
      // Get video blob from the demo video URL
      toast.info('Downloading video for processing...');
      const videoResponse = await fetch(videoRef.current.src);
      const videoBlob = await videoResponse.blob();
      
      // Create form data
      const formData = new FormData();
      formData.append('file', videoBlob, 'video.mp4');
      
      toast.info('Enhancing video with AI... This may take a minute.');
      
      // Send to backend for processing
      const response = await axios.post(`${API}/process/enhance-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      // Download the enhanced file
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `enhanced_video_${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('✅ Video enhanced! Lighting improved, stabilization applied. File downloaded.');
    } catch (error) {
      console.error('Video enhancement error:', error);
      toast.error('Video enhancement failed. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleIntroUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('video/')) {
        setIntroClip(file);
        toast.success(`Intro clip uploaded: ${file.name}`);
      } else {
        toast.error('Please upload a video file');
      }
    }
  };

  const handleOutroUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('video/')) {
        setOutroClip(file);
        toast.success(`Outro clip uploaded: ${file.name}`);
      } else {
        toast.error('Please upload a video file');
      }
    }
  };

  const handleMusicUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('audio/')) {
        setBackgroundMusic(file);
        toast.success(`Background music uploaded: ${file.name}`);
      } else {
        toast.error('Please upload an audio file');
      }
    }
  };

  const downloadAudioOnly = async () => {
    if (!videoRef.current || !videoRef.current.src) {
      toast.error('No video source available');
      return;
    }
    
    try {
      toast.info('Extracting audio from video...');
      
      // Get video blob from the demo video URL
      const videoResponse = await fetch(videoRef.current.src);
      const videoBlob = await videoResponse.blob();
      
      // Create form data
      const formData = new FormData();
      formData.append('file', videoBlob, 'video.mp4');
      
      // Send to backend for audio extraction
      const response = await axios.post(`${API}/process/extract-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      // Download the audio file
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `podcast_audio_${Date.now()}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('✅ Audio extracted! MP3 file downloaded successfully.');
    } catch (error) {
      console.error('Audio extraction error:', error);
      toast.error('Audio extraction failed. Please try again.');
    }
  };

  const renderFinalVideo = async () => {
    if (!videoRef.current || !videoRef.current.src) {
      toast.error('No video source available');
      return;
    }
    
    setIsRendering(true);
    
    try {
      toast.info('Preparing files for rendering...');
      
      // Get main video blob
      const videoResponse = await fetch(videoRef.current.src);
      const mainVideoBlob = await videoResponse.blob();
      
      // Create form data
      const formData = new FormData();
      formData.append('main_video', mainVideoBlob, 'main.mp4');
      
      // Add optional files if they exist
      if (introClip) {
        formData.append('intro_video', introClip);
      }
      if (outroClip) {
        formData.append('outro_video', outroClip);
      }
      if (backgroundMusic) {
        formData.append('background_music', backgroundMusic);
      }
      
      toast.info('Merging videos and audio... This may take a few moments.');
      
      // Send to backend for processing
      const response = await axios.post(`${API}/process/merge-videos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });
      
      // Download the final rendered video
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `final_podcast_${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('✅ Final video rendered successfully! File downloaded.');
    } catch (error) {
      console.error('Video rendering error:', error);
      toast.error('Video rendering failed. Please try again.');
    } finally {
      setIsRendering(false);
    }
  };

  const displayContent = transcript?.edited_content || transcript?.content || "";
  const words = displayContent ? displayContent.split(/\s+/) : [];
  
  // Calculate word timings (distribute evenly across video duration)
  const videoDuration = 10; // Demo video duration in seconds
  const timePerWord = videoDuration / Math.max(words.length, 1);
  
  // Handle video time update
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const handleTimeUpdate = () => {
      const time = video.currentTime;
      setCurrentTime(time);
      
      // Calculate which word should be highlighted
      const wordIndex = Math.floor(time / timePerWord);
      setCurrentWordIndex(wordIndex);
    };
    
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [timePerWord]);
  
  // Seek video to word position
  const seekToWord = (wordIndex) => {
    if (videoRef.current) {
      const targetTime = wordIndex * timePerWord;
      videoRef.current.currentTime = targetTime;
      videoRef.current.play();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Logo */}
            <div className="logo-placeholder"></div>
            <div className="border-l border-gray-200 pl-6 flex items-center gap-4">
              <Button
                data-testid="back-button"
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Studio
              </Button>
              <div>
                <h1 className="text-2xl font-bold font-manrope tracking-tight text-gray-900">Post-Production</h1>
                <p className="text-sm text-gray-600 mt-1">{recording?.title}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isPro ? (
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-md">
                <Sparkles className="w-4 h-4" />
                Pro Account
              </span>
            ) : (
              <Button
                onClick={() => setShowUpgradeModal(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Upgrade to Pro
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto space-y-6 bg-gray-50 min-h-screen">
        {/* Video Preview Section */}
        <Card className="bg-white border-gray-200 shadow-md p-6">
          <h2 className="text-xl font-semibold font-manrope mb-4 text-gray-900">Recording Preview</h2>
          <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden border border-gray-300">
            <video 
              ref={videoRef}
              controls 
              className="w-full h-full"
              poster="https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=1200&h=675&fit=crop"
            >
              <source 
                src="https://test-videos.co.uk/vids/bigbuckbunny/mp4/h264/360/Big_Buck_Bunny_360_10s_1MB.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="text-sm text-gray-600 mt-3 bg-yellow-50 border border-yellow-200 rounded p-3">
            <strong>⚠️ Demo Limitation:</strong> This is a sample video for testing the interface. For true video-transcript sync, you need to record your own podcast where the spoken words match the transcript. The interactive features (click to seek, real-time highlighting) are fully functional - just use your actual recording to see perfect sync.
          </p>
        </Card>

        {/* AI Enhancement Section */}
        <Card className="bg-white border-gray-200 shadow-md p-6">
          <h2 className="text-xl font-semibold font-manrope mb-4 text-gray-900">AI Enhancement</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className="relative bg-cover bg-center rounded-lg overflow-hidden h-48"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1764263703837-3250668eafc2)',
                backgroundSize: 'cover'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-white">Audio Enhancement</h3>
                  {!isPro && (
                    <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      PRO
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-300 mb-3">Remove noise, optimize levels, eliminate echo</p>
                <Button
                  data-testid="enhance-audio-button"
                  onClick={enhanceAudio}
                  disabled={isEnhancing}
                  className="bg-red-600 hover:bg-red-700 text-white w-full shadow-[0_0_15px_rgba(220,38,38,0.3)]"
                >
                  {isEnhancing ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {!isPro && <Lock className="w-4 h-4 mr-2" />}
                      {isPro ? <Wand2 className="w-4 h-4 mr-2" /> : null}
                      {isPro ? 'Enhance Audio' : 'Upgrade to Enhance'}
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div
              className="relative bg-cover bg-center rounded-lg overflow-hidden h-48"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1764263703837-3250668eafc2)',
                backgroundSize: 'cover'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-white">Video Enhancement</h3>
                  {!isPro && (
                    <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      PRO
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-300 mb-3">Improve lighting, stabilize footage</p>
                <Button
                  data-testid="enhance-video-button"
                  onClick={enhanceVideo}
                  disabled={isEnhancing}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                >
                  {isEnhancing ? (
                    <>
                      <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      {!isPro && <Lock className="w-4 h-4 mr-2" />}
                      {isPro ? <Wand2 className="w-4 h-4 mr-2" /> : null}
                      {isPro ? 'Enhance Video' : 'Upgrade to Enhance'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Transcript Section */}
        <Card className="bg-white border-gray-200 shadow-md p-6">
          <h2 className="text-xl font-semibold font-manrope mb-4 text-gray-900">Transcript Editor</h2>
          
          {!transcript ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">No transcript available. Create one manually:</p>
              <Textarea
                data-testid="manual-transcript-input"
                value={manualTranscript}
                onChange={(e) => setManualTranscript(e.target.value)}
                className="bg-white border-gray-300 focus:border-blue-500 h-48 font-mono text-sm"
                placeholder="Paste or type your transcript here..."
              />
              <Button
                data-testid="create-transcript-button"
                onClick={createTranscript}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create Transcript
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Interactive Transcript */}
              <div>
                <label className="text-sm text-gray-700 font-medium mb-2 block">
                  Interactive Transcript
                  <span className="text-xs text-gray-500 ml-2 font-normal">
                    (Click any word to jump to that moment in the video)
                  </span>
                </label>
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto" data-testid="transcript-display">
                  <div className="font-mono text-base leading-relaxed">
                    {words.map((word, index) => (
                      <span
                        key={index}
                        onClick={() => seekToWord(index)}
                        className={`cursor-pointer inline-block px-1 py-0.5 rounded transition-all duration-200 ${
                          index === currentWordIndex
                            ? 'bg-blue-500 text-white font-semibold shadow-sm scale-105'
                            : 'text-gray-900 hover:bg-blue-100 hover:text-blue-700'
                        }`}
                        title={`Jump to ${Math.floor(index * timePerWord)}s`}
                      >
                        {word}{' '}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                  <span>
                    Current position: {Math.floor(currentTime)}s / {videoDuration}s
                  </span>
                  <span>
                    Word {currentWordIndex + 1} of {words.length}
                  </span>
                </div>
              </div>

              {/* AI Edit Instructions */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 font-medium block">AI Edit Instructions</label>
                  {!isPro && (
                    <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      PRO
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600">Example: "Remove all instances of 'um' and 'uh'" or "Replace 'very good' with 'excellent'"</p>
                <div className="flex gap-2">
                  <Input
                    data-testid="edit-instruction-input"
                    value={editInstruction}
                    onChange={(e) => setEditInstruction(e.target.value)}
                    className="bg-white border-gray-300 focus:border-blue-500"
                    placeholder="Enter editing instruction..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isEditing) {
                        editTranscript();
                      }
                    }}
                  />
                  <Button
                    data-testid="apply-edit-button"
                    onClick={editTranscript}
                    disabled={isEditing || (!isPro && true) || (!isPro ? false : !editInstruction)}
                    className="bg-red-600 hover:bg-red-700 shadow-md whitespace-nowrap"
                  >
                    {isEditing ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                        Editing...
                      </>
                    ) : (
                      <>
                        {!isPro && <Lock className="w-4 h-4 mr-2" />}
                        {isPro ? <Wand2 className="w-4 h-4 mr-2" /> : null}
                        {isPro ? 'Apply Edit' : 'Upgrade for AI Editing'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Media Upload Section */}
        <Card className="bg-white border-gray-200 shadow-md p-6">
          <h2 className="text-xl font-semibold font-manrope mb-4 text-gray-900">Add Intro/Outro & Music</h2>
          <p className="text-sm text-gray-600 mb-4">
            Upload video clips and background music to enhance your podcast recording.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Intro Clip */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Intro Clip</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleIntroUpload}
                  className="hidden"
                  id="intro-upload"
                />
                <label htmlFor="intro-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-xs text-gray-600">
                    {introClip ? introClip.name : 'Click to upload intro'}
                  </p>
                </label>
              </div>
              {introClip && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIntroClip(null)}
                  className="w-full text-red-600 hover:text-red-700"
                >
                  Remove
                </Button>
              )}
            </div>

            {/* Outro Clip */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Outro Clip</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleOutroUpload}
                  className="hidden"
                  id="outro-upload"
                />
                <label htmlFor="outro-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-xs text-gray-600">
                    {outroClip ? outroClip.name : 'Click to upload outro'}
                  </p>
                </label>
              </div>
              {outroClip && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setOutroClip(null)}
                  className="w-full text-red-600 hover:text-red-700"
                >
                  Remove
                </Button>
              )}
            </div>

            {/* Background Music */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Background Music</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleMusicUpload}
                  className="hidden"
                  id="music-upload"
                />
                <label htmlFor="music-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-xs text-gray-600">
                    {backgroundMusic ? backgroundMusic.name : 'Click to upload music'}
                  </p>
                </label>
              </div>
              {backgroundMusic && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBackgroundMusic(null)}
                  className="w-full text-red-600 hover:text-red-700"
                >
                  Remove
                </Button>
              )}
            </div>
          </div>

          {(introClip || outroClip || backgroundMusic) && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Button
                data-testid="render-final-video-button"
                onClick={renderFinalVideo}
                disabled={isRendering}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {isRendering ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                    Rendering...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Render Final Video
                  </>
                )}
              </Button>
            </div>
          )}
        </Card>

        {/* Download Section */}
        <Card className="bg-white border-gray-200 shadow-md p-6">
          <h2 className="text-xl font-semibold font-manrope mb-4 text-gray-900">Export</h2>
          <p className="text-sm text-gray-600 mb-4">
            Download your recording in different formats for various platforms.
          </p>
          
          <div className="space-y-3">
            {/* Download Video */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Full Video (MP4)</p>
                <p className="text-xs text-gray-600">For YouTube, Vimeo, social media</p>
              </div>
              <Button
                onClick={() => toast.info('Video already saved to your computer')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Video
              </Button>
            </div>

            {/* Download Audio Only */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Audio Only (MP3)</p>
                <p className="text-xs text-gray-600">For Spotify, Apple Podcasts, podcast platforms</p>
              </div>
              <Button
                data-testid="download-audio-button"
                onClick={downloadAudioOnly}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Audio
              </Button>
            </div>

            {/* Download Transcript */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Transcript (TXT)</p>
                <p className="text-xs text-gray-600">For show notes, blog posts, SEO</p>
              </div>
              <Button
                data-testid="download-transcript-button"
                onClick={() => {
                  if (transcript) {
                    const content = displayContent;
                    const blob = new Blob([content], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `transcript-${recordingId}.txt`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    toast.success('Transcript downloaded');
                  } else {
                    toast.error('No transcript available');
                  }
                }}
                disabled={!transcript}
                className="bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Text
              </Button>
            </div>
          </div>
        </Card>
      </div>
      
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      
      <Footer />
    </div>
  );
}